/**
 * レスポンシブ・テーマ適用ユーティリティ（DOM 側）。
 */

import type { EventBus } from '../core/EventBus';
import type { StageTheme } from '../game/config/types';

export type Breakpoint = 'mobile' | 'tablet' | 'desktop';

export const BREAKPOINTS = {
  /** スマートフォン: 360px 以上 */
  mobile: 360,
  /** タブレット: 768px 以上 */
  tablet: 768,
  /** PC: 1280px 以上 */
  desktop: 1280,
} as const;

export function resolveBreakpoint(width: number): Breakpoint {
  if (width >= BREAKPOINTS.desktop) return 'desktop';
  if (width >= BREAKPOINTS.tablet) return 'tablet';
  return 'mobile';
}

/** ステージのテーマカラーを CSS 変数へ流し込む。 */
export function applyStageTheme(
  theme: StageTheme,
  root: HTMLElement = document.documentElement,
): void {
  root.style.setProperty('--stage-primary', theme.primaryColor);
  root.style.setProperty('--stage-secondary', theme.secondaryColor);
  root.style.setProperty('--stage-accent', theme.accentColor);
  root.style.setProperty('--stage-background', theme.backgroundColor);
}

/**
 * ゲーム内の時間帯（夕暮れ→薄暮→夜）を DOM 側へ伝える。
 *
 * CSS の `html[data-time='...']` で body の背景色を切り替えることで、
 * Canvas の中の情景と画面全体の空気感がつながって見える。
 */
export function watchScenePhase(
  bus: EventBus,
  root: HTMLElement = document.documentElement,
): () => void {
  root.dataset.time = 'dusk';
  return bus.on('scene:phase', ({ phase }) => {
    root.dataset.time = phase;
  });
}

/**
 * 画面幅の変化を監視し、data-layout 属性を更新する。
 * CSS 側のメディアクエリと併用して、JS からもレイアウトを判別できるようにする。
 */
export function watchLayout(
  target: HTMLElement,
  onChange?: (breakpoint: Breakpoint) => void,
): () => void {
  let current: Breakpoint | null = null;

  const apply = () => {
    const next = resolveBreakpoint(window.innerWidth);
    if (next === current) return;
    current = next;
    target.dataset.layout = next;
    onChange?.(next);
  };

  apply();
  window.addEventListener('resize', apply, { passive: true });
  window.addEventListener('orientationchange', apply);

  return () => {
    window.removeEventListener('resize', apply);
    window.removeEventListener('orientationchange', apply);
  };
}

/**
 * ゲームエリア内のタッチ操作でページがスクロールしないようにする。
 * （touch-action: none だけでは不十分な端末があるため保険をかける）
 */
export function lockTouchScroll(element: HTMLElement): () => void {
  const prevent = (event: TouchEvent) => {
    if (event.cancelable) event.preventDefault();
  };
  element.addEventListener('touchstart', prevent, { passive: false });
  element.addEventListener('touchmove', prevent, { passive: false });
  element.addEventListener('touchend', prevent, { passive: false });

  return () => {
    element.removeEventListener('touchstart', prevent);
    element.removeEventListener('touchmove', prevent);
    element.removeEventListener('touchend', prevent);
  };
}
